import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Alert,
  Grid
} from '@mui/material';
import {
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

const FinalVerification = ({ releaseData, tracks, trackMetadata, royaltySplits, coverArt }) => {
  const [verificationResults, setVerificationResults] = useState({
    metadata: { pass: false, issues: [] },
    tracks: { pass: false, issues: [] },
    royalties: { pass: false, issues: [] },
    artwork: { pass: false, issues: [] },
    legal: { pass: false, issues: [] }
  });

  useEffect(() => {
    const validateMetadata = () => {
      const issues = [];
      
      if (!releaseData.title) {
        issues.push('Release title is required');
      }
      if (!releaseData.primaryGenre) {
        issues.push('Primary genre is required');
      }
      if (!releaseData.releaseDate) {
        issues.push('Release date is required');
      }

      return {
        pass: issues.length === 0,
        issues
      };
    };

    const validateTracks = () => {
      const issues = [];
      
      if (tracks.length === 0) {
        issues.push('No tracks added to the release');
        return { pass: false, issues };
      }

      tracks.forEach((track, index) => {
        const metadata = trackMetadata[track.file.name];
        if (!metadata) {
          issues.push(`Track ${index + 1}: Missing metadata`);
        } else {
          if (!metadata.trackType) {
            issues.push(`Track ${index + 1}: Track type not specified`);
          }
          // Removed the composers check since it's optional
        }
      });

      return {
        pass: issues.length === 0,
        issues
      };
    };

    const validateRoyalties = () => {
      // Skip royalty validation entirely when step is hidden
      // This allows releases to be submitted without royalty configuration
      return {
        pass: true,
        issues: []
      };
    };

    const validateArtwork = () => {
      const issues = [];
      
      if (!coverArt) {
        issues.push('Cover artwork is required');
      } else {
        if (coverArt.type !== 'image/jpeg' && coverArt.type !== 'image/png') {
          issues.push('Cover artwork must be JPEG or PNG format');
        }
        // Size validation should already be handled in the CoverArtwork component
      }

      return {
        pass: issues.length === 0,
        issues
      };
    };

    const validateLegal = () => {
      const issues = [];
      
      // Check for copyright control or publishing info
      tracks.forEach((track, index) => {
        const metadata = trackMetadata[track.file.name];
        if (!metadata) return;

        if (!metadata.publishing.isCopyrightControl && 
            (!metadata.publishing.house || !metadata.publishing.rights)) {
          issues.push(`Track ${index + 1}: Publishing information required when not under copyright control`);
        }
      });

      return {
        pass: issues.length === 0,
        issues
      };
    };

    const results = {
      metadata: validateMetadata(),
      tracks: validateTracks(),
      royalties: validateRoyalties(),
      artwork: validateArtwork(),
      legal: validateLegal()
    };
    setVerificationResults(results);
  }, [releaseData, tracks, trackMetadata, royaltySplits, coverArt]);

  const getStatusIcon = (status) => {
    if (status.pass) {
      return <CheckIcon color="success" />;
    }
    return status.issues.length > 3 ? 
      <ErrorIcon color="error" /> : 
      <WarningIcon color="warning" />;
  };

  const allChecksPass = Object.entries(verificationResults)
    .filter(([section]) => section !== 'royalties') // Exclude royalty verification from overall check
    .every(([, result]) => result.pass);

  return (
    <Box>
      {!allChecksPass && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please resolve all issues before submitting your release.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Release Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {releaseData.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {releaseData.type.toUpperCase()} • {tracks.length} tracks
              </Typography>
              <Chip 
                label={releaseData.primaryGenre} 
                size="small" 
                sx={{ mt: 1 }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          {Object.entries(verificationResults)
            .filter(([section]) => section !== 'royalties') // Hide royalty verification section
            .map(([section, status]) => (
            <Accordion key={section} defaultExpanded={!status.pass}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ListItemIcon>
                    {getStatusIcon(status)}
                  </ListItemIcon>
                  <Typography>
                    {section.charAt(0).toUpperCase() + section.slice(1)} Verification
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {status.issues.length > 0 ? (
                  <List dense>
                    {status.issues.map((issue, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ErrorIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={issue} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="success.main">
                    All checks passed
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinalVerification;